/**
 * Depth-vs-days plan curve — cumulative planned days (X) vs cumulative measured
 * depth (Y), one marker per program phase. Built on the shared ChartFrame
 * (§15): ChartFrame owns the responsive viewBox, padding, border and axis
 * labels + hands us value→pixel scales, so this only plots the polyline,
 * markers, Y grid/ticks and X phase ticks. Colored by the event's operation token.
 */
import { useLocale } from "@hooks/useLocale";
import { ChartFrame } from "@components/chart/ChartFrame";
import type { CurvePoint } from "./workspaceModel";

export function PlanCurve({ points, opVar }: { points: CurvePoint[]; opVar: string }) {
  const { t } = useLocale();
  const maxDays = Math.max(...points.map((p) => p.days), 1);
  const maxMd = Math.max(...points.map((p) => p.md), 1);
  const stroke = `var(${opVar})`;

  return (
    <div className="plan-curve" style={{ marginTop: 16 }}>
      <ChartFrame
        title={t("ws.curve.title")}
        xDomain={[0, maxDays]}
        yDomain={[0, maxMd]}
        yDown
        height={240}
        xLabel={t("ws.curve.xAxis")}
        yLabel={t("ws.curve.yAxis")}
        ariaLabel={t("ws.curve.aria")}
      >
        {({ plot, sx, sy }) => {
          const poly = points
            .map((p) => `${sx(p.days).toFixed(1)},${sy(p.md).toFixed(1)}`)
            .join(" ");
          return (
            <>
              {/* Y grid + scale: 0 → deepest MD in five bands. */}
              <g className="scale-txt" textAnchor="end">
                {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                  const md = maxMd * f;
                  return (
                    <g key={i}>
                      <line
                        x1={plot.x}
                        y1={sy(md)}
                        x2={plot.x + plot.w}
                        y2={sy(md)}
                        stroke="var(--line)"
                        strokeWidth={0.5}
                        opacity={0.6}
                      />
                      <text x={plot.x - 4} y={sy(md) + 3}>
                        {Math.round(md).toLocaleString()}
                      </text>
                    </g>
                  );
                })}
              </g>
              <polyline fill="none" stroke={stroke} strokeWidth={2.5} points={poly} />
              <g fill={stroke}>
                {points.map((p, i) => (
                  <circle key={i} cx={sx(p.days)} cy={sy(p.md)} r={3.5} />
                ))}
              </g>
              {/* X ticks at each phase boundary. */}
              <g className="scale-txt" textAnchor="middle" style={{ fontSize: 8.5 }}>
                {points.map((p, i) => (
                  <text key={i} x={sx(p.days)} y={plot.y + plot.h + 14}>
                    D{Number.isInteger(p.days) ? p.days : p.days.toFixed(1)}
                  </text>
                ))}
              </g>
            </>
          );
        }}
      </ChartFrame>
      <div className="legend">
        <span className="s" style={{ ["--series" as string]: stroke }}>
          {t("ws.curve.series")}
        </span>
      </div>
    </div>
  );
}
